<template>
  <!--
    TASK-SF-07 -- the 3-step redesigned student onboarding wizard. This is a
    STATE of LoginView (mobileState === 'STUDENT_ONBOARDING'), not a route:
    src/router.js is NO CHANGE and there is no /onboarding/student path.

    Steps are an in-component index, so browser Back does not traverse them
    (02_* §3.6). Values are drafted to sessionStorage (SF-06) keyed to the
    registration grant and restored on remount; the parent clears the draft
    on success and on every terminal outcome.
  -->
  <div
    class="student-onboarding-wizard"
    data-testid="student-onboarding-wizard"
  >
    <!--
      TASK-SF-07 visual parity: Borna-only in-card branding with a hairline
      divider beneath it (prototype `.auth-brand-pair--borna-only`). The
      foundation co-brand is DESIGN-01 only; the wizard shows the Borna mark
      alone.
    -->
    <div class="onboarding-brand" data-testid="onboarding-brand">
      <img
        class="onboarding-brand__mark"
        src="@/assets/logo/borna-identity.png"
        alt="برنا"
      />
    </div>

    <onboarding-progress :step="step" :total="3" />

    <p
      v-if="loadError"
      class="alert alert-danger"
      role="alert"
      data-testid="onboarding-reference-error"
    >
      {{ $t('login.studentOnboarding.referenceError') }}
    </p>

    <step-identity
      v-if="step === 1"
      :value="identityValue"
      @input="mergeForm"
      @next="goToStep(2)"
      @back="$emit('back-to-role')"
    />

    <step-academics
      v-else-if="step === 2"
      :value="academicsValue"
      :stages="stages"
      @input="mergeForm"
      @next="goToStep(3)"
      @back="goToStep(1)"
    />

    <step-optional
      v-else-if="step === 3"
      :value="optionalValue"
      :email-error="emailError"
      :submitting="submitting"
      @input="mergeForm"
      @submit="onSubmit"
      @back="goToStep(2)"
      @clear-email-conflict="$emit('clear-email-conflict')"
    />
  </div>
</template>

<script>
import StudentService from '@/services/StudentService'
import { saveDraft, loadDraft } from '@/utils/onboarding-draft'
import OnboardingProgress from './OnboardingProgress'
import StepIdentity from './StepIdentity'
import StepAcademics from './StepAcademics'
import StepOptional from './StepOptional'

function emptyForm() {
  return {
    firstName: '',
    lastName: '',
    educationStage: '',
    educationGrade: '',
    studyField: '',
    schoolName: '',
    email: '',
  }
}

export default {
  name: 'StudentOnboardingWizard',
  components: {
    OnboardingProgress,
    StepIdentity,
    StepAcademics,
    StepOptional,
  },
  props: {
    // The verified registration grant the draft is keyed to.
    grantId: { type: String, default: null },
    // Server EMAIL_CONFLICT message; when set the wizard must HOLD on step 3.
    emailError: { type: String, default: null },
    submitting: { type: Boolean, default: false },
  },
  data() {
    return {
      step: 1,
      form: emptyForm(),
      stages: [],
      loadError: false,
    }
  },
  computed: {
    // Read-only slices of `form`. Each step emits a DELTA ({ key: value }),
    // and `mergeForm` applies it against the always-fresh `form` -- so a
    // second field's change can never carry a stale copy of the first.
    identityValue() {
      return { firstName: this.form.firstName, lastName: this.form.lastName }
    },
    academicsValue() {
      return {
        educationStage: this.form.educationStage,
        educationGrade: this.form.educationGrade,
        studyField: this.form.studyField,
      }
    },
    optionalValue() {
      return { schoolName: this.form.schoolName, email: this.form.email }
    },
  },
  watch: {
    // Any change to the form re-persists the draft against the current grant.
    form: {
      deep: true,
      handler() {
        this.persist()
      },
    },
    emailError(val) {
      // A late email collision must not unwind the wizard.
      if (val) this.step = 3
    },
  },
  created() {
    const restored = loadDraft(this.grantId)
    if (restored && typeof restored === 'object') {
      this.form = { ...emptyForm(), ...restored }
    }
    this.fetchReference()
  },
  methods: {
    async fetchReference() {
      try {
        const data = await StudentService.getEducationReference()
        this.stages = Array.isArray(data.stages) ? data.stages : []
        this.loadError = false
      } catch (e) {
        this.loadError = true
      }
    },
    mergeForm(patch) {
      this.form = { ...this.form, ...patch }
    },
    persist() {
      saveDraft(this.grantId, { ...this.form })
    },
    goToStep(n) {
      this.step = n
      this.persist()
    },
    onSubmit() {
      // Emit slugs/contracts the backend accepts; blank optionals are
      // dropped by AuthService's allowlist, so passing '' is harmless.
      this.$emit('submit', {
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        educationStage: this.form.educationStage,
        educationGrade: this.form.educationGrade,
        studyField: this.form.studyField,
        schoolName: this.form.schoolName,
        email: this.form.email,
      })
    },
  },
}
</script>

<!--
  TASK-SF-07 visual parity. NOT scoped: every rule in the partial is nested
  under `.student-onboarding-wizard` (this component's root), so it reaches
  the step children's `.uc-form-*` / `.DropdownList-*` hooks without editing
  the shared `_form.scss` / `DropdownList.vue`, and reaches nothing outside
  the wizard. The volunteer `REGISTRATION_FORM` never mounts this element.
-->
<style lang="scss">
@import '@/scss/components/student-onboarding';
</style>
